/**
 * Shared formatters for the fleet console. Kept colocated so changes
 * to display conventions (decimal places, units, "zero" rendering)
 * ripple across all screens consistently.
 */
import type { Stream } from '@/api/fleet';

export const formatUsd = (n: number | undefined | null): string => {
  if (!n || n <= 0) return '$0.00';
  if (n < 0.01) return '<$0.01';
  if (n < 1) return `$${n.toFixed(4)}`;
  if (n < 1000) return `$${n.toFixed(2)}`;
  return `$${Math.round(n).toLocaleString()}`;
};

export const formatRev = (n: number | undefined | null): string => {
  if (!n || n <= 0) return '0';
  if (n < 1) return n.toFixed(4);
  return Math.round(n).toLocaleString();
};

export const formatHashrate = (h: number | undefined | null): string => {
  if (!h) return '0 H/s';
  if (h >= 1e9) return `${(h / 1e9).toFixed(2)} GH/s`;
  if (h >= 1e6) return `${(h / 1e6).toFixed(2)} MH/s`;
  if (h >= 1e3) return `${(h / 1e3).toFixed(2)} KH/s`;
  return `${h} H/s`;
};

export const formatBytes = (b: number | undefined | null): string => {
  if (!b) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

export const formatUptime = (seconds: number | undefined | null): string => {
  if (!seconds) return '—';
  const d = Math.floor(seconds / 86_400);
  const h = Math.floor((seconds % 86_400) / 3_600);
  if (d > 0) return `${d}d ${h}h`;
  const m = Math.floor((seconds % 3_600) / 60);
  return `${h}h ${m}m`;
};

export const timeAgo = (date: string | Date | undefined | null): string => {
  if (!date) return 'Never';
  const diff = (Date.now() - new Date(date).getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3_600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86_400) return `${Math.floor(diff / 3_600)}h ago`;
  return `${Math.floor(diff / 86_400)}d ago`;
};

export const STREAM_META: Record<
  Stream,
  { label: string; emoji: string; color: string; accent: string }
> = {
  cpu_compute: {
    label: 'CPU Compute',
    emoji: '🧠',
    color: 'text-purple-300',
    accent: 'from-purple-500/20 to-purple-700/10'
  },
  gpu_compute: {
    label: 'GPU + Inference',
    emoji: '🎮',
    color: 'text-emerald-300',
    accent: 'from-emerald-500/20 to-emerald-700/10'
  },
  storage: {
    label: 'Storage',
    emoji: '💾',
    color: 'text-amber-300',
    accent: 'from-amber-500/20 to-amber-700/10'
  },
  bandwidth: {
    label: 'Bandwidth',
    emoji: '📡',
    color: 'text-sky-300',
    accent: 'from-sky-500/20 to-sky-700/10'
  },
  mixed: {
    label: 'Mixed',
    emoji: '🔀',
    color: 'text-nurev-muted',
    accent: 'from-slate-500/20 to-slate-700/10'
  }
};

export const PLATFORM_META: Record<
  'linux' | 'windows' | 'mac',
  { label: string; emoji: string }
> = {
  linux: { label: 'Linux', emoji: '🐧' },
  windows: { label: 'Windows', emoji: '🪟' },
  mac: { label: 'macOS', emoji: '🍎' }
};

export const STATUS_BADGE: Record<string, string> = {
  active: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  paused: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  offline: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30',
  decommissioned: 'bg-slate-500/20 text-slate-400 ring-1 ring-slate-500/30'
};
